'use client';
import 'client-only';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MAKE_TO_COUNTRY, COUNTRY_FLAG } from '@/lib/manufacturers';
import {
  Card,
  Chip,
  Eyebrow,
  HotPill,
} from '../../_components/primitives';

const API = process.env.NEXT_PUBLIC_MO_YOUTUBE_FEED_API!;
const PAGE_LIMIT = 30;

interface Channel {
  channel_id: string;
  title: string;
  custom_url: string | null;
  thumbnail_url: string | null;
}

interface ChannelStats {
  total_videos: number;
  video_types: { video_type: string; count: number }[];
  top_makes: { make: string; count: number }[];
  top_models: { make: string; model: string; count: number }[];
  review_depths: { review_depth: string; count: number }[];
}

interface VideoItem {
  video_id: string;
  title: string;
  published_at: string;
  thumbnail_url: string | null;
  link: string;
  video_type?: string;
  review_depth?: string;
  vehicles_mentioned?: string;
  channel_title?: string;
  channel_custom_url?: string;
  channel_thumbnail_url?: string;
}

interface VehicleCluster {
  make: string;
  model: string;
  start_date: string;
  end_date: string;
  channel_count: number;
  video_count: number;
}

interface FilterState {
  make?: string;
  model?: string;
  videoType?: string;
  country?: string;
}

const VIDEO_TYPE_LABELS: Record<string, string> = {
  single_vehicle_review: 'Review',
  comparison: 'Comparison',
  first_look: 'First Look',
  news: 'News',
  entertainment: 'Entertainment',
  other: 'Other',
};

/* Segmented Type pills above the list. Trimmed to four primary
   categories — "Reviews only" and "Comparisons only" are redundant
   with the default "Reviews & Comparisons" pill for the page's intent
   (latest reviews) and would push the segmented row to overflow. */
const TYPE_PILLS = [
  { value: 'reviews', label: 'Reviews & Comparisons' },
  { value: 'first_look', label: 'First Looks' },
  { value: 'news', label: 'News' },
  { value: 'all', label: 'All types' },
];

/* Paint-ish palette for country bars — mapped to the site's brand-
   neighbour colors rather than the random scheme prod uses, so the
   chart stays visually coordinated with the rest of the page. */
const COUNTRY_COLOR: Record<string, string> = {
  Japan: '#E23F36' /* rallye-red-ish */,
  Germany: 'var(--denim)',
  USA: '#84A71E' /* coral-deep */,
  'South Korea': '#8B5CF6',
  UK: 'var(--coral-deep)',
  Italy: '#06B6D4',
  Sweden: 'var(--denim-400)',
  France: '#EC4899',
  China: '#F97316',
};

function parseVehicles(
  json?: string,
): { make: string; model: string; year: string; role: string }[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function formatDate(isoString: string): string {
  const d = new Date(isoString);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'today';
  const yesterday = new Date(now.getTime() - 86400000);
  if (d.toDateString() === yesterday.toDateString()) return 'yesterday';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

function makeLogoUrl(make: string): string | null {
  const domains: Record<string, string> = {
    Honda: 'honda.com',
    Acura: 'acura.com',
    Toyota: 'toyota.com',
    Lexus: 'lexus.com',
    Mazda: 'mazda.com',
    Subaru: 'subaru.com',
    Nissan: 'nissanusa.com',
    Infiniti: 'infinitiusa.com',
    Mitsubishi: 'mitsubishi-motors.com',
    Suzuki: 'suzuki.com',
    BMW: 'bmw.com',
    Mercedes: 'mercedes-benz.com',
    'Mercedes-Benz': 'mercedes-benz.com',
    Audi: 'audi.com',
    Porsche: 'porsche.com',
    Volkswagen: 'volkswagen.com',
    VW: 'volkswagen.com',
    MINI: 'mini.com',
    Mini: 'mini.com',
    Ford: 'ford.com',
    Chevrolet: 'chevrolet.com',
    Dodge: 'dodge.com',
    Cadillac: 'cadillac.com',
    GMC: 'gmc.com',
    Tesla: 'tesla.com',
    Rivian: 'rivian.com',
    Lincoln: 'lincoln.com',
    Jeep: 'jeep.com',
    Chrysler: 'chrysler.com',
    Hyundai: 'hyundai.com',
    Kia: 'kia.com',
    Genesis: 'genesis.com',
    Jaguar: 'jaguar.com',
    'Land Rover': 'landrover.com',
    'Aston Martin': 'astonmartin.com',
    McLaren: 'mclaren.com',
    Lotus: 'lotuscars.com',
    Bentley: 'bentleymotors.com',
    'Rolls-Royce': 'rolls-roycemotorcars.com',
    Volvo: 'volvocars.com',
    Polestar: 'polestar.com',
    Ferrari: 'ferrari.com',
    Lamborghini: 'lamborghini.com',
    Maserati: 'maserati.com',
    'Alfa Romeo': 'alfaromeo.com',
    Fiat: 'fiat.com',
    Renault: 'renault.com',
    Peugeot: 'peugeot.com',
  };
  const domain = domains[make];
  return domain
    ? `https://www.google.com/s2/favicons?domain=${domain}&sz=32`
    : null;
}

function MakeLogo({ make, size = 16 }: { make: string; size?: number }) {
  const url = makeLogoUrl(make);
  if (!url) return null;
  return (
    <img
      src={url}
      alt={make}
      style={{
        width: size,
        height: size,
        borderRadius: 2,
        objectFit: 'contain',
        display: 'inline-block',
        flexShrink: 0,
      }}
    />
  );
}

export function ChannelContent({ slug }: { slug: string }) {
  const isAll = slug === '@all' || slug === 'all';

  const [channel, setChannel] = useState<Channel | null>(null);
  const [stats, setStats] = useState<ChannelStats | null>(null);
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [clusters, setClusters] = useState<VehicleCluster[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filters, setFilters] = useState<FilterState>(
    isAll ? { videoType: 'reviews' } : {},
  );
  const [makeModels, setMakeModels] = useState<
    { model: string; count: number }[]
  >([]);

  /* Apply ?make=Porsche&model=911 query params on first mount. */
  const [paramsApplied, setParamsApplied] = useState(false);
  useEffect(() => {
    if (paramsApplied) return;
    const params = new URLSearchParams(window.location.search);
    const make = params.get('make') ?? undefined;
    const model = params.get('model') ?? undefined;
    if (make || model) {
      setFilters((prev) => ({ ...prev, make, model }));
    }
    setParamsApplied(true);
  }, [paramsApplied]);

  /* Mobile collapse — at <820px the grid stacks (sidebar above list).
     We hide the sidebar contents behind a "Filters" toggle so the
     video stream sits at the top of the viewport. Type pills stay
     pinned above the list at all widths. */
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 820px)');
    setIsNarrow(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  /* Resolve slug → channel + load stats. Per-channel uses
     /channels/by-slug/:slug + /channels/:id/stats; all-view uses /stats
     directly with a synthetic channel record. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isAll) {
        setChannel({
          channel_id: '__all__',
          title: 'Latest Car Reviews on YouTube',
          custom_url: '@all',
          thumbnail_url: null,
        });
        const statsRes = await fetch(`${API}/stats`);
        if (statsRes.ok && !cancelled) setStats(await statsRes.json());
      } else {
        const res = await fetch(`${API}/channels/by-slug/${slug}`);
        if (!res.ok || cancelled) {
          setLoading(false);
          return;
        }
        const ch: Channel = await res.json();
        if (cancelled) return;
        setChannel(ch);
        const statsRes = await fetch(`${API}/channels/${ch.channel_id}/stats`);
        if (statsRes.ok && !cancelled) setStats(await statsRes.json());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, isAll]);

  /* Cluster strip is whole-feed signal — only fetch on /channel/all. */
  useEffect(() => {
    if (!isAll) return;
    fetch(`${API}/clusters`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) setClusters(data);
      })
      .catch(() => {});
  }, [isAll]);

  /* Per-make model dropdown population (sidebar facets). */
  useEffect(() => {
    if (!filters.make || !channel) {
      setMakeModels([]);
      return;
    }
    const url = isAll
      ? `${API}/models?make=${encodeURIComponent(filters.make)}`
      : `${API}/channels/${channel.channel_id}/models?make=${encodeURIComponent(filters.make)}`;
    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) setMakeModels(data);
      })
      .catch(() => {});
  }, [filters.make, channel, isAll]);

  /* Build params used by both the page-1 fetch (filter changes) and
     the load-more path. Lifted from filter+stats so URL composition
     stays consistent. */
  const buildParams = useCallback(
    (newOffset: number): URLSearchParams => {
      const params = new URLSearchParams();
      if (channel && !isAll) params.set('channel_id', channel.channel_id);
      params.set('limit', String(PAGE_LIMIT));
      params.set('offset', String(newOffset));
      if (filters.make) {
        params.set('make', filters.make);
      } else if (filters.country && stats) {
        const makes = stats.top_makes
          .filter((m) => MAKE_TO_COUNTRY[m.make] === filters.country)
          .map((m) => m.make);
        if (makes.length > 0) params.set('makes', makes.join(','));
      }
      if (filters.model) params.set('model', filters.model);
      if (filters.videoType === 'reviews') {
        params.set('video_types', 'single_vehicle_review,comparison');
      } else if (filters.videoType && filters.videoType !== 'all') {
        params.set('video_type', filters.videoType);
      }
      return params;
    },
    [channel, isAll, filters, stats],
  );

  /* Page-1 fetch on filter / channel change. */
  useEffect(() => {
    if (!channel || !stats) return;
    setLoading(true);
    const params = buildParams(0);
    fetch(`${API}/videos?${params}`)
      .then((r) => r.json())
      .then((data: VideoItem[]) => {
        if (Array.isArray(data)) {
          setVideos(data);
          setHasMore(data.length === PAGE_LIMIT);
          setOffset(0);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [channel, stats, buildParams]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const newOffset = offset + PAGE_LIMIT;
    const params = buildParams(newOffset);
    try {
      const res = await fetch(`${API}/videos?${params}`);
      if (!res.ok) {
        setLoadingMore(false);
        return;
      }
      const data: VideoItem[] = await res.json();
      if (Array.isArray(data)) {
        setVideos((prev) => [...prev, ...data]);
        setHasMore(data.length === PAGE_LIMIT);
        setOffset(newOffset);
      }
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, offset, buildParams]);

  /* Sync make/model filter changes back to the URL so links stay
     bookmarkable and shareable (matches legacy behavior). */
  const updateUrlParams = useCallback((f: FilterState) => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams();
    if (f.make) params.set('make', f.make);
    if (f.model) params.set('model', f.model);
    const qs = params.toString();
    const newUrl = `${window.location.pathname}${qs ? '?' + qs : ''}`;
    window.history.replaceState(null, '', newUrl);
  }, []);

  const onFilterChange = useCallback(
    (partial: Partial<FilterState>) => {
      setFilters((prev) => {
        const next = { ...prev, ...partial };
        updateUrlParams(next);
        return next;
      });
    },
    [updateUrlParams],
  );

  const hasFilters = !!(
    filters.make ||
    filters.model ||
    filters.country ||
    (filters.videoType && filters.videoType !== 'reviews')
  );
  /* Clear-button visibility — only Country/Make/Model qualify. The
     Type segmented bar always has one pill active (default = reviews),
     so it's never in a "needs clearing" state; clearing it would just
     bounce back to the default pill. */
  const hasFacetFilters = !!(filters.make || filters.model || filters.country);

  const countries = useMemo(() => {
    if (!stats) return [];
    const map = new Map<string, number>();
    for (const { make, count } of stats.top_makes) {
      const c = MAKE_TO_COUNTRY[make];
      if (c) map.set(c, (map.get(c) ?? 0) + count);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([country, count]) => ({ country, count }));
  }, [stats]);

  const activeCountry = filters.make
    ? MAKE_TO_COUNTRY[filters.make]
    : filters.country;

  const deduped = useMemo(() => {
    return clusters.reduce<VehicleCluster[]>((acc, c) => {
      const existing = acc.find(
        (a) => a.make === c.make && a.model === c.model,
      );
      if (existing) {
        if (c.start_date < existing.start_date)
          existing.start_date = c.start_date;
        if (c.end_date > existing.end_date) existing.end_date = c.end_date;
        existing.video_count += c.video_count;
        existing.channel_count = Math.max(
          existing.channel_count,
          c.channel_count,
        );
      } else {
        acc.push({ ...c });
      }
      return acc;
    }, []);
  }, [clusters]);

  const playAllHref = useMemo(
    () =>
      videos.length > 0
        ? `https://www.youtube.com/watch_videos?video_ids=${videos
            .map((v) => v.video_id)
            .join(',')}`
        : null,
    [videos],
  );

  if (!channel && !loading) {
    return (
      <div
        className="design-videos"
        style={{
          maxWidth: 1152,
          margin: '0 auto',
          padding:
            'clamp(0px, 0.5vw, 6px) calc(clamp(12px, 1.8vw, 20px) + 4px) 80px',
          color: 'var(--fg-3)',
          fontFamily: 'var(--font-sans)',
        }}
      >
        Channel not found.
      </div>
    );
  }

  return (
    <div
      className="design-videos"
      style={{
        maxWidth: 1152,
        margin: '0 auto',
        /* Horizontal padding matches the Nav's inner rail
           (clamp(12px, 1.8vw, 20px) + 4px wordmark inset = 24px at
           desktop) so body content aligns with the wordmark and the
           first tab. Vertical padding stays content-generous. */
        padding:
          'clamp(0px, 0.5vw, 6px) calc(clamp(12px, 1.8vw, 20px) + 4px) 80px',
      }}
    >
      {/* Header */}
      <ChannelHeader channel={channel} stats={stats} isAll={isAll} />

      {/* "What's hot right now" — algorithmically-detected cluster
          strip. Only on /channel/all (whole-feed signal). */}
      {isAll && deduped.length > 0 && (
        <ClusterStrip
          clusters={deduped}
          activeMake={filters.make}
          activeModel={filters.model}
          onPick={(make, model) =>
            onFilterChange(
              filters.make === make && filters.model === model
                ? { make: undefined, model: undefined }
                : { make, model, country: undefined },
            )
          }
        />
      )}

      {/* Two-column layout: facet sidebar + video list.
          Single filter surface — Country/Make/Model live in the
          sidebar facets (which already carry counts + a country bar
          chart, so they earn their column width). Type filter is
          promoted to a segmented pill bar above the list since it's
          the most-used and benefits from a glanceable, always-visible
          control. */}
      <div className="design-videos-grid" style={{ marginTop: 18 }}>
        {/* Facet sidebar */}
        <aside className="design-videos-sidebar">
          {/* Mobile: collapse the facets behind a "Filters" button so
              the video stream sits at the top of the viewport. Desktop
              renders the facets directly. */}
          {isNarrow && (
            <button
              type="button"
              onClick={() => setFiltersOpen((o) => !o)}
              aria-expanded={filtersOpen}
              className="mo-bg-card"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '10px 14px',
                border: '1px solid var(--border)',
                borderRadius: 10,
                color: 'var(--fg-1)',
                fontFamily: 'var(--font-sans)',
                fontWeight: 500,
                fontSize: 13,
                cursor: 'pointer',
                boxShadow: 'var(--card-stack)',
                marginBottom: filtersOpen ? 10 : 0,
              }}
            >
              <span style={{ flex: '1 1 auto', textAlign: 'left' }}>
                Filters
                {hasFilters && (
                  <span
                    className="mo-bg-denim-soft"
                    style={{
                      marginLeft: 6,
                      fontFamily: 'var(--font-mono)',
                      fontVariantNumeric: 'tabular-nums',
                      fontSize: 11,
                      color: 'var(--denim-deep)',
                      padding: '1px 6px',
                      borderRadius: 999,
                    }}
                  >
                    {(filters.country ? 1 : 0) +
                      (filters.make ? 1 : 0) +
                      (filters.model ? 1 : 0)}
                  </span>
                )}
              </span>
              <span
                aria-hidden
                style={{
                  display: 'inline-block',
                  transition: 'transform 160ms ease',
                  transform: filtersOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  color: 'var(--fg-3)',
                  fontSize: 11,
                }}
              >
                ▾
              </span>
            </button>
          )}
          {(!isNarrow || filtersOpen) && stats && (
            <>
              <FacetCard title="By Country">
                <CountryBars
                  countries={countries}
                  active={activeCountry}
                  onPick={(country) => {
                    if (activeCountry === country) {
                      onFilterChange({
                        country: undefined,
                        make: undefined,
                        model: undefined,
                      });
                    } else {
                      onFilterChange({
                        country,
                        make: undefined,
                        model: undefined,
                      });
                    }
                  }}
                />
              </FacetCard>
              <FacetCard title="Top Makes">
                {stats.top_makes
                  .filter(
                    (m) =>
                      !activeCountry ||
                      MAKE_TO_COUNTRY[m.make] === activeCountry,
                  )
                  .slice(0, 10)
                  .map(({ make, count }) => (
                    <FacetRow
                      key={make}
                      label={make}
                      icon={<MakeLogo make={make} />}
                      count={count}
                      active={filters.make === make}
                      onClick={() =>
                        onFilterChange({
                          make: filters.make === make ? undefined : make,
                          model: undefined,
                          country: undefined,
                        })
                      }
                    />
                  ))}
              </FacetCard>
              <FacetCard title="Top Models">
                {(filters.make
                  ? makeModelsAsTopModels(filters.make, makeModels, stats)
                  : stats.top_models
                )
                  .filter((m) =>
                    filters.make
                      ? m.make === filters.make
                      : !activeCountry ||
                        MAKE_TO_COUNTRY[m.make] === activeCountry,
                  )
                  .slice(0, 10)
                  .map(({ make, model, count }) => (
                    <FacetRow
                      key={`${make}-${model}`}
                      label={`${make} ${model}`}
                      icon={<MakeLogo make={make} />}
                      count={count}
                      active={filters.make === make && filters.model === model}
                      onClick={() =>
                        onFilterChange({
                          make:
                            filters.make === make && filters.model === model
                              ? undefined
                              : make,
                          model:
                            filters.make === make && filters.model === model
                              ? undefined
                              : model,
                          country: undefined,
                        })
                      }
                    />
                  ))}
              </FacetCard>
            </>
          )}
        </aside>

        {/* Video list */}
        <main>
          {/* Type segmented pill bar + meta line. Always-visible
              primary control. Single rounded group with shared
              internal dividers (geometry mirrors the
              CarIconSegment treatment on /design/stock/civic-type-r). */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 12,
              marginBottom: 14,
              paddingBottom: 12,
              borderBottom: '1px solid var(--border-soft)',
            }}
          >
            <div
              role="tablist"
              aria-label="Video type"
              className="mo-bg-card"
              style={{
                display: 'inline-flex',
                border: '1px solid var(--border)',
                borderRadius: 999,
                overflow: 'hidden',
                boxShadow: 'var(--card-stack)',
              }}
            >
              {TYPE_PILLS.map((opt, i) => {
                const on = (filters.videoType ?? 'reviews') === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    onClick={() => onFilterChange({ videoType: opt.value })}
                    style={{
                      padding: '7px 14px',
                      border: 0,
                      borderLeft: i > 0 ? '1px solid var(--border)' : 0,
                      background: on ? 'var(--denim-100)' : 'transparent',
                      color: on ? 'var(--denim-deep)' : 'var(--fg-2)',
                      fontFamily: 'var(--font-sans)',
                      fontSize: 12.5,
                      fontWeight: on ? 600 : 500,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'background 120ms ease, color 120ms ease',
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <span
              style={{
                marginLeft: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              {hasFacetFilters && (
                <button
                  type="button"
                  onClick={() => {
                    const next: FilterState = {
                      videoType: filters.videoType ?? 'reviews',
                      make: undefined,
                      model: undefined,
                      country: undefined,
                    };
                    setFilters(next);
                    updateUrlParams(next);
                  }}
                  aria-label="Clear country, make, and model filters"
                  title="Clear filters"
                  className="mo-bg-card"
                  style={{
                    height: 28,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '0 12px 0 10px',
                    border: '1px solid var(--border)',
                    borderRadius: 999,
                    color: 'var(--fg-2)',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 11.5,
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  {/* filter-x (Lucide). Funnel with a strike — reads as
                      "filter, off". 13px sits comfortably with the
                      11.5px label. */}
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M13.013 3H2l8 9.46V19l4 2v-8.54l.9-1.055" />
                    <path d="m22 3-5 5" />
                    <path d="m17 3 5 5" />
                  </svg>
                  Clear
                </button>
              )}
              {playAllHref && (
                <a
                  href={playAllHref}
                  target="_blank"
                  rel="noreferrer"
                  className="mo-bg-coral-deep"
                  style={{
                    height: 28,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '0 12px',
                    borderRadius: 999,
                    color: '#fff',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 11.5,
                    fontWeight: 600,
                    textDecoration: 'none',
                    letterSpacing: '0.02em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  ▶ play all ({videos.length}
                  {hasMore ? '+' : ''})
                </a>
              )}
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: 12,
                  color: 'var(--fg-3)',
                }}
              >
                {videos.length} videos
              </span>
            </span>
          </div>

          {loading ? (
            <div
              style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: 'var(--fg-3)',
                fontFamily: 'var(--font-sans)',
              }}
            >
              Loading videos…
            </div>
          ) : videos.length === 0 ? (
            <div
              style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: 'var(--fg-3)',
                fontFamily: 'var(--font-sans)',
              }}
            >
              No videos match these filters.
            </div>
          ) : (
            <>
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
              >
                {videos.map((v) => (
                  <VideoRow key={v.video_id} video={v} showChannel={isAll} />
                ))}
              </div>
              {hasMore && (
                <div
                  style={{
                    marginTop: 16,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="mo-bg-card"
                    style={{
                      padding: '8px 18px',
                      borderRadius: 999,
                      border: '1px solid var(--border)',
                      color: 'var(--fg-2)',
                      fontFamily: 'var(--font-sans)',
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: loadingMore ? 'wait' : 'pointer',
                      opacity: loadingMore ? 0.6 : 1,
                      boxShadow: 'var(--card-stack)',
                    }}
                  >
                    {loadingMore ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

/* Top Models facet shows make-scoped model list when a make filter is
   active (richer counts from /models?make=…). When no make filter,
   we fall back to the cross-make `top_models` from /stats. */
function makeModelsAsTopModels(
  make: string,
  rows: { model: string; count: number }[],
  stats: ChannelStats,
): { make: string; model: string; count: number }[] {
  if (rows.length > 0) return rows.map((r) => ({ make, ...r }));
  return stats.top_models;
}

function ChannelHeader({
  channel,
  stats,
  isAll,
}: {
  channel: Channel | null;
  stats: ChannelStats | null;
  isAll: boolean;
}) {
  if (!channel) return null;

  /* All-channels view: matches the original /design/videos page
     header — eyebrow + Playfair display headline + meta. */
  if (isAll) {
    return (
      <header style={{ marginBottom: 20 }}>
        <div style={{ marginBottom: 4 }}>
          <Eyebrow>Car Reviews · All Channels</Eyebrow>
        </div>
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 200,
            fontSize: 'clamp(28px, 4vw, 40px)',
            letterSpacing: '-0.015em',
            lineHeight: 1.1,
            color: 'var(--fg-1)',
            margin: 0,
          }}
        >
          Latest reviews on YouTube
        </h1>
        {stats && (
          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 300,
              fontSize: 14,
              color: 'var(--fg-3)',
              marginTop: 6,
            }}
          >
            {stats.total_videos.toLocaleString()} videos indexed across 200+
            automotive channels.
          </p>
        )}
      </header>
    );
  }

  /* Per-channel view: breadcrumb + channel thumbnail + title + meta. */
  return (
    <header style={{ marginBottom: 20 }}>
      <nav
        aria-label="Breadcrumb"
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          color: 'var(--fg-3)',
          marginBottom: 10,
          letterSpacing: '0.02em',
        }}
      >
        <a
          href="/channel/all"
          style={{ color: 'var(--fg-3)', textDecoration: 'none' }}
        >
          car reviews
        </a>
        <span style={{ margin: '0 6px' }}>·</span>
        <a
          href="/channel"
          style={{ color: 'var(--fg-3)', textDecoration: 'none' }}
        >
          all channels
        </a>
        <span style={{ margin: '0 6px' }}>·</span>
        <span style={{ color: 'var(--fg-2)' }}>{channel.title}</span>
      </nav>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {channel.thumbnail_url && (
          <img
            src={channel.thumbnail_url}
            alt={channel.title}
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              flexShrink: 0,
              boxShadow: 'var(--card-stack)',
            }}
          />
        )}
        <div style={{ minWidth: 0 }}>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 200,
              fontSize: 'clamp(26px, 3.4vw, 36px)',
              letterSpacing: '-0.015em',
              lineHeight: 1.1,
              color: 'var(--fg-1)',
              margin: 0,
            }}
          >
            {channel.title}
          </h1>
          {stats && (
            <p
              style={{
                fontFamily: 'var(--font-sans)',
                fontWeight: 300,
                fontSize: 13,
                color: 'var(--fg-3)',
                marginTop: 4,
              }}
            >
              {stats.total_videos.toLocaleString()} videos indexed.
            </p>
          )}
        </div>
      </div>
    </header>
  );
}

function FacetCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card padded={false} style={{ marginBottom: 10 }}>
      <div style={{ padding: '12px 14px 10px' }}>
        <Eyebrow size="sm">{title}</Eyebrow>
        <div
          style={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {children}
        </div>
      </div>
    </Card>
  );
}

function FacetRow({
  label,
  icon,
  count,
  active,
  onClick,
}: {
  label: string;
  icon?: React.ReactNode;
  count: number;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        width: '100%',
        padding: '4px 8px',
        border: 0,
        borderRadius: 6,
        background: active ? 'var(--denim-ink)' : 'transparent',
        color: active ? 'var(--fg-inverse)' : 'var(--fg-2)',
        fontFamily: 'var(--font-sans)',
        fontSize: 13,
        fontWeight: 400,
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      {icon}
      <span
        style={{
          flex: '1 1 auto',
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          fontVariantNumeric: 'tabular-nums',
          color: active ? 'rgba(255,255,255,0.7)' : 'var(--fg-4)',
        }}
      >
        {count}
      </span>
    </button>
  );
}

function CountryBars({
  countries,
  active,
  onPick,
}: {
  countries: { country: string; count: number }[];
  active?: string;
  onPick: (country: string) => void;
}) {
  const max = Math.max(...countries.map((c) => c.count), 1);
  return (
    <>
      {countries.map(({ country, count }) => {
        const pct = count / max;
        const isActive = active === country;
        const barColor = COUNTRY_COLOR[country] ?? 'var(--fg-4)';
        return (
          <button
            type="button"
            key={country}
            onClick={() => onPick(country)}
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              width: '100%',
              padding: '4px 8px',
              border: 0,
              borderRadius: 6,
              background: isActive ? 'var(--denim-ink)' : 'transparent',
              color: isActive ? 'var(--fg-inverse)' : 'var(--fg-2)',
              overflow: 'hidden',
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            {!isActive && (
              <span
                aria-hidden
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${pct * 100}%`,
                  background: barColor,
                  opacity: 0.18,
                  borderRadius: 6,
                }}
              />
            )}
            <span style={{ position: 'relative', flexShrink: 0 }}>
              {COUNTRY_FLAG[country] ?? ''}
            </span>
            <span style={{ position: 'relative', flex: '1 1 auto' }}>
              {country}
            </span>
            <span
              style={{
                position: 'relative',
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                fontVariantNumeric: 'tabular-nums',
                color: isActive ? 'rgba(255,255,255,0.7)' : 'var(--fg-4)',
              }}
            >
              {count}
            </span>
          </button>
        );
      })}
    </>
  );
}

/* Final "what's hot right now" cluster strip.
   - Expanded 16/300 eyebrow in denim-ink (presence through space).
   - Solid 4px coral-deep top border (signals featured, no AI-gradient
     cliché, no construction-tape association).
   - HotPill copy + active-pill fill handled inline. */
function ClusterStrip({
  clusters,
  activeMake,
  activeModel,
  onPick,
  pillCopy = 'Trending this week',
}: {
  clusters: VehicleCluster[];
  activeMake?: string;
  activeModel?: string;
  onPick: (make: string, model: string) => void;
  pillCopy?: string;
}) {
  return (
    <div style={{ position: 'relative', marginBottom: 18 }}>
      <Card padded>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 12,
          }}
        >
          <Eyebrow size="lg" tone="denim">
            What&apos;s hot
          </Eyebrow>
          <HotPill>{pillCopy}</HotPill>
        </div>
        <ClusterPills
          clusters={clusters}
          activeMake={activeMake}
          activeModel={activeModel}
          onPick={onPick}
        />
      </Card>
      <div
        aria-hidden
        className="mo-bg-coral-deep"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          borderTopLeftRadius: 'var(--radius-card, 12px)',
          borderTopRightRadius: 'var(--radius-card, 12px)',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}

function ClusterPills({
  clusters,
  activeMake,
  activeModel,
  onPick,
}: {
  clusters: VehicleCluster[];
  activeMake?: string;
  activeModel?: string;
  onPick: (make: string, model: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {clusters.map((c) => {
        const isActive = activeMake === c.make && activeModel === c.model;
        return (
          <button
            type="button"
            key={`${c.make}-${c.model}`}
            onClick={() => onPick(c.make, c.model)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 10px 5px 8px',
              borderRadius: 999,
              border: isActive
                ? '1px solid transparent'
                : '1px solid var(--border)',
              background: isActive ? 'var(--coral-deep)' : 'var(--card-bg)',
              color: isActive ? '#fff' : 'var(--fg-2)',
              fontFamily: 'var(--font-sans)',
              fontSize: 12.5,
              fontWeight: 500,
              cursor: 'pointer',
              boxShadow: isActive ? 'var(--shadow-sm)' : 'var(--card-stack)',
            }}
          >
            <MakeLogo make={c.make} size={14} />
            <span>
              {c.make} {c.model}
            </span>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontVariantNumeric: 'tabular-nums',
                fontSize: 10.5,
                padding: '0 6px',
                borderRadius: 999,
                background: isActive ? 'rgba(0,0,0,0.12)' : 'var(--denim-soft)',
                color: isActive ? '#fff' : 'var(--denim-deep)',
                minWidth: 16,
                textAlign: 'center',
              }}
            >
              {c.channel_count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function VideoRow({
  video,
  showChannel,
}: {
  video: VideoItem;
  showChannel?: boolean;
}) {
  const vehicles = parseVehicles(video.vehicles_mentioned).filter(
    (v) => v.role === 'primary' || v.role === 'comparison',
  );
  /* Per-channel view doesn't need to repeat the channel name on every
     row — it's already in the page header. */
  const channelHref = video.channel_custom_url
    ? `/channel/${video.channel_custom_url.replace('@', '').toLowerCase()}`
    : null;
  return (
    <a
      href={video.link}
      target="_blank"
      rel="noreferrer"
      style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
    >
      <Card padded={false}>
        <div
          className="design-videocard"
          style={{
            display: 'grid',
            gridTemplateColumns: '168px 1fr',
            gap: 12,
            minHeight: 100,
            alignItems: 'stretch',
          }}
        >
          <div
            className="mo-bg-fg-1"
            style={{
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {video.thumbnail_url && (
              <img
                src={video.thumbnail_url.replace(
                  '/hqdefault.jpg',
                  '/mqdefault.jpg',
                )}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            )}
          </div>
          <div
            style={{
              padding: '10px 14px 10px 0',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              justifyContent: 'space-between',
            }}
          >
            <div
              style={{
                fontFamily: 'var(--font-sans)',
                fontWeight: 500,
                fontSize: 14.5,
                lineHeight: 1.35,
                color: 'var(--fg-1)',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {video.title}
            </div>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {showChannel && video.channel_thumbnail_url && (
                <img
                  src={video.channel_thumbnail_url}
                  alt=""
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    flexShrink: 0,
                  }}
                />
              )}
              {showChannel && video.channel_title && (
                <span
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: 12,
                    color: 'var(--fg-2)',
                  }}
                >
                  {channelHref ? (
                    /* Stop the wrapping anchor so the inner channel link
                       wins — clicking the channel name should navigate
                       within the site, not pop YouTube. */
                    <span
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.location.href = channelHref;
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {video.channel_title}
                    </span>
                  ) : (
                    video.channel_title
                  )}
                </span>
              )}
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10.5,
                  color: 'var(--fg-4)',
                }}
              >
                {formatDate(video.published_at)}
              </span>
              {video.video_type && (
                <Chip
                  tone={
                    video.video_type === 'single_vehicle_review'
                      ? 'apex'
                      : video.video_type === 'comparison'
                        ? 'denim'
                        : 'slate'
                  }
                >
                  {VIDEO_TYPE_LABELS[video.video_type] ?? video.video_type}
                </Chip>
              )}
              {vehicles.slice(0, 3).map((v, i) => (
                <Chip key={i} tone="outline">
                  {[v.year, v.make, v.model].filter(Boolean).join(' ')}
                </Chip>
              ))}
            </div>
          </div>
        </div>
      </Card>
    </a>
  );
}
